import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import type { WishlistCreateDraft, WishlistCreateFieldErrors, WishlistCreateOccasion } from '@nexus/customer/wishlists/model';
import { WISHLIST_CREATE_OCCASION_LABELS } from '@nexus/customer/wishlists/util';
import { UiChipComponent, UiInputComponent, UiTextareaComponent } from '@nexus/frontend-shared-ui';

interface OccasionOption {
  readonly id: WishlistCreateOccasion;
  readonly label: string;
}

@Component({
  selector: 'wishlist-create-basic-info-form',
  imports: [UiChipComponent, UiInputComponent, UiTextareaComponent],
  templateUrl: './wishlist-create-basic-info-form.component.html',
  styleUrl: './wishlist-create-basic-info-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistCreateBasicInfoFormComponent {
  public readonly draft: InputSignal<WishlistCreateDraft> = input.required<WishlistCreateDraft>();
  public readonly errors: InputSignal<WishlistCreateFieldErrors> = input.required<WishlistCreateFieldErrors>();
  public readonly titleChange: OutputEmitterRef<string> = output<string>();
  public readonly descriptionChange: OutputEmitterRef<string> = output<string>();
  public readonly occasionChange: OutputEmitterRef<WishlistCreateOccasion | null> =
    output<WishlistCreateOccasion | null>();
  public readonly customOccasionLabelChange: OutputEmitterRef<string> = output<string>();
  public readonly occasionDateChange: OutputEmitterRef<string> = output<string>();

  protected readonly occasionOptions: Signal<readonly OccasionOption[]> = computed(
    (): readonly OccasionOption[] =>
      (['birthday', 'holidays', 'layette', 'sport', 'home', 'none', 'custom'] as const).map(
        (occasion: WishlistCreateOccasion): OccasionOption => ({
          id: occasion,
          label: WISHLIST_CREATE_OCCASION_LABELS[occasion],
        }),
      ),
  );

  protected handleTitleChange(value: string): void {
    this.titleChange.emit(value);
  }

  protected handleDescriptionChange(value: string): void {
    this.descriptionChange.emit(value);
  }

  protected handleOccasionSelected(occasion: WishlistCreateOccasion): void {
    const selectedOccasion: WishlistCreateOccasion | null = this.draft().occasion === occasion ? null : occasion;
    this.occasionChange.emit(selectedOccasion);
  }

  protected handleCustomOccasionLabelChange(value: string): void {
    this.customOccasionLabelChange.emit(value);
  }

  protected handleOccasionDateChange(value: string): void {
    this.occasionDateChange.emit(value);
  }
}
